import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, PlusCircle, LogOut, Search, Filter, Eye, Trash2, Calendar, Loader2, RefreshCw, ClipboardList, Copy, Sparkles, Settings } from 'lucide-react';
import Logo from '../../components/common/Logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import apiService from '../../services/api';

const AdminReportsListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar dados do backend
  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const reportsData = await apiService.getReports();
      
      // Converter dados do backend para formato do frontend
      const convertedReports = reportsData.map(r => ({
        id: r.id,
        babyName: r.baby_name || 'Não informado',
        motherName: r.mother_name || 'Não informado',
        email: r.email || '',
        babyAge: r.baby_age || '-',
        status: r.status || 'pending_form',
        createdAt: r.created_at ? r.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
        completedDays: r.completed_days || 0
      }));
      
      setReports(convertedReports);
    } catch (err) {
      console.error('Erro ao carregar relatórios:', err);
      setError('Erro ao carregar relatórios.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const handleDeleteReport = async (reportId, e) => {
    e.stopPropagation();
    if (window.confirm('Tem certeza que deseja excluir este relatório?')) {
      try {
        await apiService.deleteReport(reportId);
        loadReports(); // Recarregar lista
      } catch (err) {
        alert('Erro ao excluir relatório.');
      }
    }
  };

  const statusLabels = {
    pending_form: { label: 'Aguardando formulário', color: 'bg-gray-100 text-gray-600' },
    pending_routine: { label: 'Registrando rotina', color: 'bg-yellow-100 text-yellow-700' },
    pending_analysis: { label: 'Aguardando análise', color: 'bg-blue-100 text-blue-700' },
    pending_approval: { label: 'Aguardando aprovação', color: 'bg-purple-100 text-purple-700' },
    approved: { label: 'Aprovado', color: 'bg-green-100 text-green-700' }
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = 
      report.babyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.motherName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || report.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-[#FBF4FF]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-[#FAE0D5] p-6 hidden lg:block">
        <div className="mb-8">
          <Link to="/admin/dashboard">
            <Logo variant="v3SonoRotina" size="md" className="cursor-pointer hover:opacity-80 transition-opacity" />
          </Link>
        </div>

        <nav className="space-y-2">
          {[
            { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
            { icon: PlusCircle, label: 'Novo Relatório', path: '/admin/new' },
            { icon: ClipboardList, label: 'Inserir Manualmente', path: '/admin/manual' },
            { icon: FileText, label: 'Relatórios', path: '/admin/reports' },
            { icon: Copy, label: 'Templates', path: '/admin/templates' },
            { icon: Sparkles, label: 'Treinar IA', path: '/admin/training' },
            { icon: Settings, label: 'Editor de Formulário', path: '/admin/form-builder' }
          ].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                location.pathname === item.path
                  ? 'bg-[#8F7AC5] text-white'
                  : 'text-[#6B5B8C] hover:bg-[#FBF4FF]'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#9890A8] hover:text-[#30244F] hover:bg-[#FBF4FF]"
            onClick={() => navigate('/admin')}
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden mb-6 flex items-center justify-between">
          <Link to="/admin/dashboard">
            <Logo variant="v3SonoRotina" size="sm" />
          </Link>
        </div>

        {/* Page Header */}
        <motion.div
          className="mb-8 flex items-center justify-between"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#30244F]">Relatórios</h1>
            <p className="text-[#6B5B8C]">Gerencie todos os relatórios de rotina</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={loadReports}
            disabled={loading}
            className="text-[#8F7AC5]"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </motion.div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9890A8]" />
            <Input
              placeholder="Buscar por nome do bebê, mãe ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-[#FAE0D5]"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[200px] border-[#FAE0D5]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrar status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="pending_routine">Registrando rotina</SelectItem>
              <SelectItem value="pending_analysis">Aguardando análise</SelectItem>
              <SelectItem value="pending_approval">Aguardando aprovação</SelectItem>
              <SelectItem value="approved">Aprovado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Reports Table */}
        <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#8F7AC5] animate-spin" />
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FBF4FF]">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#30244F]">Bebê</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#30244F]">Mãe</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#30244F]">Idade</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#30244F]">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#30244F]">Dias</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#30244F]">Criado em</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-[#30244F]">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-[#9890A8]">
                      Nenhum relatório encontrado
                    </td>
                  </tr>
                ) : (
                  filteredReports.map((report, i) => (
                    <motion.tr
                      key={report.id}
                      className="border-t border-[#FAE0D5] hover:bg-[#FBF4FF]/50 transition-colors cursor-pointer"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      onClick={() => navigate(`/admin/reports/${report.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-[#D98DAE] to-[#8F7AC5] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                            {report.babyName?.charAt(0) || '?'}
                          </div>
                          <span className="font-medium text-[#30244F]">{report.babyName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[#6B5B8C]">{report.motherName}</td>
                      <td className="px-6 py-4 text-[#6B5B8C]">{report.babyAge} meses</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusLabels[report.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                          {statusLabels[report.status]?.label || report.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[#6B5B8C]">{report.completedDays}/5</td>
                      <td className="px-6 py-4 text-[#6B5B8C]">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {report.createdAt}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/reports/${report.id}`); }}
                            className="text-[#8F7AC5] hover:bg-[#8F7AC5]/10"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteReport(report.id, e)}
                            className="text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminReportsListPage;
